import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { Amount } from "@/components/shared/Amount";
import { AHeader, ACard, ATable, inputDark } from "@/components/shared/AdminKit";
import { useConfirm } from "@/context/ConfirmContext";
import { StatusBadge } from "@/components/shared/Badges";
import { fmtDate } from "@/components/shared/DashKit";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Search, Eye, X, RefreshCw, Coins } from "lucide-react";

const sLabel = (t) => ({
  waiting: t("admin.usdt.st.waiting", "En attente"), confirming: t("admin.usdt.st.confirming", "Confirmation"),
  confirmed: t("admin.usdt.st.confirmed", "Confirmé"), sending: t("admin.usdt.st.sending", "Envoi"),
  partially_paid: t("admin.usdt.st.partial", "Partiel"), finished: t("admin.usdt.st.finished", "Terminé"),
  completed: t("admin.usdt.st.completed", "Crédité"), failed: t("admin.usdt.st.failed", "Échoué"),
  refunded: t("admin.usdt.st.refunded", "Remboursé"), expired: t("admin.usdt.st.expired", "Expiré"),
});
const short = (h) => (h && h.length > 16 ? `${h.slice(0, 8)}…${h.slice(-6)}` : h);

export default function AdminUSDT() {
  const { t } = useContent();
  const STATUSES = sLabel(t);
  const [sp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(sp.get("q") || "");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    if (status) p.set("status", status);
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    api.get(`/admin/payments/usdt${p.toString() ? `?${p}` : ""}`)
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [q, status, dateFrom, dateTo]);

  useEffect(() => { const tm = setTimeout(load, 300); return () => clearTimeout(tm); }, [load]);

  return (
    <div>
      <AHeader title={t("admin.usdt.title", "USDT — Crypto (TRC20)")} subtitle={t("admin.usdt.subtitle", "Transactions USDT réelles via NOWPayments. Crédit uniquement après confirmation on-chain réelle.")} />

      <ACard className="mb-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4" data-testid="usdt-filters">
          <div className="relative md:col-span-2">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input data-testid="usdt-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={t("admin.usdt.search_ph", "Recherche : réf. TWP, e-mail, tx hash, ID…")} className={`${inputDark} pl-9`} />
          </div>
          <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="usdt-status-filter" className="border-slate-700 bg-slate-800 text-white"><SelectValue placeholder={t("admin.usdt.status_ph", "Statut")} /></SelectTrigger>
            <SelectContent><SelectItem value="all">{t("admin.usdt.all_statuses", "Tous les statuts")}</SelectItem>{Object.entries(STATUSES).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <div className="flex gap-2">
            <input data-testid="usdt-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={inputDark} title={t("admin.usdt.from", "Du")} />
            <input data-testid="usdt-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={inputDark} title={t("admin.usdt.to", "Au")} />
          </div>
        </div>
      </ACard>

      <ACard>
        {loading ? <div className="py-10 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-accent" /></div>
          : items.length === 0 ? <div className="py-10 text-center text-slate-400" data-testid="usdt-empty">{t("admin.usdt.empty", "Aucune transaction USDT.")}</div>
          : (
            <ATable head={[t("admin.usdt.th.ref", "Réf. TWP"), t("admin.usdt.th.client", "Client"), t("admin.usdt.th.amount", "Montant"), t("admin.usdt.th.network", "Réseau"), t("admin.usdt.th.paid", "Payé"), t("admin.usdt.th.txhash", "Tx hash"), t("admin.usdt.th.status", "Statut"), t("admin.usdt.th.confirmed", "Confirmé le"), t("admin.usdt.th.date", "Date"), ""]}>
              {items.map((p) => (
                <tr key={p.id} className="border-b border-slate-800/60 last:border-0" data-testid={`usdt-row-${p.id}`}>
                  <td className="px-3 py-3 font-mono text-xs text-slate-200">{p.reference}</td>
                  <td className="px-3 py-3 text-slate-400">{p.user_email}</td>
                  <td className="px-3 py-3 font-medium"><Amount value={p.amount} /></td>
                  <td className="px-3 py-3 text-slate-400">{p.network}</td>
                  <td className="px-3 py-3 text-slate-400">{p.pay_amount ? `${p.pay_amount} ${(p.pay_currency || "USDT").toUpperCase()}` : "—"}</td>
                  <td className="px-3 py-3 font-mono text-xs text-slate-400">{p.tx_hash ? short(p.tx_hash) : "—"}</td>
                  <td className="px-3 py-3"><StatusBadge status={p.credited ? "completed" : p.status} /></td>
                  <td className="px-3 py-3 text-slate-400">{p.confirmed_at ? fmtDate(p.confirmed_at) : "—"}</td>
                  <td className="px-3 py-3 text-slate-400">{fmtDate(p.created_at)}</td>
                  <td className="px-3 py-3">
                    <button onClick={() => setDetail(p)} data-testid={`usdt-detail-${p.id}`}
                      className="inline-flex items-center gap-1 rounded-full border border-slate-700 px-2.5 py-1 text-xs text-slate-200 hover:bg-slate-800"><Eye className="h-3 w-3" /> {t("admin.usdt.details", "Détails")}</button>
                  </td>
                </tr>
              ))}
            </ATable>
          )}
      </ACard>

      {detail && <UsdtDetail p={detail} onClose={() => setDetail(null)} onReconciled={() => { setDetail(null); load(); }} />}
    </div>
  );
}

function UsdtDetail({ p, onClose, onReconciled }) {
  const { t } = useContent();
  const STATUSES = sLabel(t);
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);
  const reconcile = async () => {
    const desc = t("admin.usdt.verify_desc", "Vérifier ce paiement auprès de NOWPayments et créditer uniquement s'il est réellement confirmé on-chain ?\nRéf. : {ref}").replace("{ref}", p.reference);
    if (!(await confirm({ title: t("admin.usdt.verify_title", "Vérifier le paiement USDT"), description: desc, confirmLabel: t("admin.usdt.verify_ok", "Vérifier"), cancelLabel: t("admin.usdt.cancel", "Annuler") }))) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/payments/usdt/reconcile", { payment_id: p.id });
      if (data.credited) toast.success(`${t("admin.usdt.credited", "Crédité :")} ${data.amount} $`);
      else toast.info(data.message || t("admin.usdt.not_credited", "Non crédité"));
      onReconciled();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const Row = ({ label, value }) => (
    <div className="flex justify-between gap-4 border-b border-slate-800/50 py-2 text-sm last:border-0">
      <span className="text-slate-400">{label}</span><span className="text-right font-medium text-slate-100 break-all">{value ?? "—"}</span>
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-lg overflow-auto rounded-xl border border-slate-800 bg-slate-900 p-6" onClick={(e) => e.stopPropagation()} data-testid="usdt-detail-modal">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-white"><Coins className="h-5 w-5 text-accent" /> {t("admin.usdt.tx_title", "Transaction USDT")}</h3>
          <button onClick={onClose} data-testid="usdt-detail-close" className="text-slate-400 hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        <Row label={t("admin.usdt.row.ref", "Réf. TWP")} value={p.reference} />
        <Row label={t("admin.usdt.row.tx_id", "ID transaction")} value={p.id} />
        <Row label={t("admin.usdt.row.client", "Client")} value={p.user_email} />
        <Row label={t("admin.usdt.row.amount_credited", "Montant crédité")} value={`${p.amount} ${p.currency}`} />
        <Row label={t("admin.usdt.row.network", "Réseau")} value={p.network} />
        <Row label={t("admin.usdt.row.pay_amount", "Montant à payer")} value={p.pay_amount ? `${p.pay_amount} ${(p.pay_currency || "USDT").toUpperCase()}` : "—"} />
        <Row label={t("admin.usdt.row.actually_paid", "Réellement payé")} value={p.actually_paid != null ? `${p.actually_paid}` : "—"} />
        <Row label={t("admin.usdt.row.pay_address", "Adresse de dépôt")} value={p.pay_address} />
        <Row label={t("admin.usdt.row.txhash", "Tx hash")} value={p.tx_hash} />
        <Row label={t("admin.usdt.row.np_id", "ID paiement NOWPayments")} value={p.np_payment_id} />
        <Row label={t("admin.usdt.row.status", "Statut")} value={<StatusBadge status={p.credited ? "completed" : p.status} />} />
        <Row label={t("admin.usdt.row.credited_wallet", "Crédité au Wallet")} value={p.credited ? t("admin.usdt.yes", "Oui") : t("admin.usdt.no", "Non")} />
        <Row label={t("admin.usdt.row.created", "Créé le")} value={fmtDate(p.created_at)} />
        <Row label={t("admin.usdt.row.confirmed", "Confirmé le")} value={p.confirmed_at ? fmtDate(p.confirmed_at) : "—"} />
        {p.error && <div className="mt-3 rounded-lg border border-red-900/50 bg-red-950/40 px-3 py-2 text-xs text-red-300" data-testid="usdt-detail-error">{t("admin.usdt.error", "Erreur :")} {p.error}</div>}

        <div className="mt-4">
          <p className="mb-1 text-xs font-medium text-slate-400">{t("admin.usdt.status_history", "Historique du statut")}</p>
          <div className="space-y-1" data-testid="usdt-detail-history">
            {(p.status_history || []).map((h, i) => (
              <div key={i} className="flex justify-between text-xs text-slate-300"><span>{STATUSES[h.status] || h.status}</span><span className="text-slate-500">{h.at ? fmtDate(h.at) : "—"}</span></div>
            ))}
          </div>
        </div>

        {!p.credited && (
          <button onClick={reconcile} disabled={busy} data-testid="usdt-detail-reconcile"
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-accent px-4 py-2.5 text-sm font-medium text-white disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {t("admin.usdt.reconcile_btn", "Vérifier & créditer (via NOWPayments)")}
          </button>
        )}
      </div>
    </div>
  );
}
